import { Signal, TemplateRef } from '@angular/core';
import {
  CellTemplateContext,
  ColumnBuilder,
  DatasetTableConfig,
  TableConfigBuilder,
  formattedColumn,
  editAction,
  deleteAction,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { DoorModels } from '@models/door.model';

export type DoorCellContext = CellTemplateContext<DoorModels.Door>;

export interface CreateDoorsTableConfigDependencies {
  imageCellTemplate: TemplateRef<DoorCellContext>;
  nameCellTemplate: TemplateRef<DoorCellContext>;
  getLocationName: (row: DoorModels.Door) => string;
  getCameraName: (row: DoorModels.Door) => string;
  editingId: Signal<string | null>;
}

export function createDoorsTableConfig(
  dependencies: CreateDoorsTableConfigDependencies,
): DatasetTableConfig<DoorModels.Door> {
  const builder = new ColumnBuilder<DoorModels.Door>();

  return new TableConfigBuilder<DoorModels.Door>()
    .columns([
      builder
        .column('id')
        .label('')
        .detailsModalLabel('DOORS.TABLE.IMAGE')
        .width('minmax(80px, 0.8fr)')
        .cellTemplate(dependencies.imageCellTemplate)
        .build(),

      builder
        .column('name')
        .label('DOORS.TABLE.NAME')
        .width('minmax(150px, 1fr)')
        .cellTemplate(dependencies.nameCellTemplate)
        .mobileVisible()
        .cardBoldValue()
        .build(),

      formattedColumn<DoorModels.Door>(
        'locationId',
        'DOORS.TABLE.LOCATION',
        (_, row) => dependencies.getLocationName(row),
        {
          width: 'minmax(120px, 0.8fr)',
          mobileVisible: true,
          cardIcon: 'ui-icon-location',
        },
      ),

      formattedColumn<DoorModels.Door>(
        'cameraId',
        'DOORS.TABLE.CAMERA',
        (_, row) => dependencies.getCameraName(row) ?? 'N/A',
        {
          width: 'minmax(120px, 0.8fr)',
        },
      ),
    ])
    .actions([
      editAction<DoorModels.Door>({
        tooltip: (row) => {
          const editingId = dependencies.editingId();
          return editingId === row.id ? 'COMMON.SAVE' : 'COMMON.EDIT';
        },
        visible: (row) => {
          const editingId = dependencies.editingId();
          return editingId !== row.id;
        },
      }),
      {
        id: 'cancel',
        icon: 'ui-icon-close',
        tooltip: 'COMMON.CANCEL',
        visible: (row) => {
          const editingId = dependencies.editingId();
          return editingId === row.id;
        },
      },
      {
        id: 'save',
        icon: 'ui-icon-save',
        tooltip: 'COMMON.SAVE',
        visible: (row) => {
          const editingId = dependencies.editingId();
          return editingId === row.id;
        },
      },
      deleteAction<DoorModels.Door>({
        tooltip: 'COMMON.DELETE',
      }),
    ])
    .trackBy('id')
    .rowHeight(72)
    .build();
}
